import type { MilestoneStatus } from '../../data/types';
import { Icon, type IconName } from '../chrome/Icon';

/* Shared filter + group-by controls, used by both the Milestones list and the
 * embedded/standalone Timeline so the two views stay in sync. */

import { useT } from '../../i18n';

export type Filter = 'all' | 'now' | 'soon' | 'done' | 'open';
export type GroupBy = 'category' | 'leap' | 'date';

export const FILTERS: { id: Filter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'all',  label: 'All' },
  { id: 'now',  label: 'On track' },
  { id: 'soon', label: 'Upcoming' },
  { id: 'done', label: 'Achieved' },
];

export const GROUPS: { id: GroupBy; label: string }[] = [
  { id: 'category', label: 'Category' },
  { id: 'leap',     label: 'Leaps' },
  { id: 'date',     label: 'Date' },
];

/** Whether a milestone with `status` should be shown under the given filter. */
export function passesFilter(status: MilestoneStatus, filter: Filter): boolean {
  if (filter === 'all') return true;
  if (filter === 'open') return status !== 'done';
  return status === filter;
}

interface SelectProps<T extends string> {
  icon: IconName;
  ariaLabel: string;
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string }[];
}

function Select<T extends string>({ icon, ariaLabel, value, onChange, options }: SelectProps<T>) {
  const t = useT();
  const optionLabel = (id: string, def: string) => {
    if (['open', 'all', 'now', 'soon', 'done'].includes(id)) {
      return t('milestones.filter.' + id, def);
    }
    if (['category', 'leap', 'date'].includes(id)) {
      return t('milestones.group.' + id, def);
    }
    return def;
  };

  return (
    <div className="relative flex min-h-9 flex-1 items-center gap-2 rounded-xl border border-line bg-surface pl-3 pr-8">
      <Icon name={icon} size={15} color="var(--color-ink-3)" />
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full cursor-pointer appearance-none bg-transparent py-2 text-[13px] font-semibold text-ink outline-none"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {optionLabel(o.id, o.label)}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2.5">
        <Icon name="down" size={15} color="var(--color-ink-3)" />
      </span>
    </div>
  );
}

interface ControlBarProps {
  filter: Filter;
  setFilter: (f: Filter) => void;
  groupBy: GroupBy;
  setGroupBy: (g: GroupBy) => void;
  className?: string;
}

/** Filter + Group-by dropdowns, side by side on one line. */
export function ControlBar({ filter, setFilter, groupBy, setGroupBy, className }: ControlBarProps) {
  const t = useT();
  return (
    <div className={['flex items-center gap-2', className ?? ''].join(' ')}>
      <Select
        icon="filter"
        ariaLabel={t('milestones.filter.aria', 'Filter milestones')}
        value={filter}
        onChange={setFilter}
        options={FILTERS}
      />
      <Select
        icon="list"
        ariaLabel={t('milestones.group.aria', 'Group milestones by')}
        value={groupBy}
        onChange={setGroupBy}
        options={GROUPS}
      />
    </div>
  );
}
